import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Package, Clock, DollarSign, CreditCard, LogOut, MapPin, Eye, User, Phone, Mail } from 'lucide-react';

import { getApiUrl, isBackendConfigured } from '../config';

const formatDeliveryDate = (dateStr) => {
  if (!dateStr) return 'Date TBD';
  const parts = dateStr.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString();
  }
  return new Date(dateStr).toLocaleDateString();
};

const formatDeliveryTime = (delivery) => {
  if (!delivery) return 'Time TBD';
  return delivery.timeLabel || delivery.time || 'Time TBD';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(getApiUrl('/api/orders'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchOrders();
  }, [navigate, fetchOrders]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        getApiUrl(`/api/orders/${orderId}/status`),
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Refresh orders
      fetchOrders();
      
      // Update selected order if it's open
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({...selectedOrder, status: newStatus});
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-neutral-600';
      case 'completed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getCustomerName = (order) => {
    const name = [order.customerFirstName, order.customerLastName].filter(Boolean).join(' ');
    return name || 'Guest checkout';
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === filterStatus);

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-neutral-950 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-300 mt-1">Tribal Dash Order Management</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-neutral-950"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Processing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-neutral-700">{stats.processing}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Orders</CardTitle>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">{order.id}</h3>
                          <Badge className={getStatusColor(order.status)}>
                            {order.status.toUpperCase()}
                          </Badge>
                          {order.payment?.paymentStatus === 'pending_manual' && (
                            <Badge className="bg-amber-500">CHARGE AT STORE</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Customer</p>
                            <p className="font-semibold">{getCustomerName(order)}</p>
                            {order.customerEmail && (
                              <p className="text-xs text-gray-500 truncate">{order.customerEmail}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-600">Items</p>
                            <p className="font-semibold">{order.items.length} products</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Total</p>
                            <p className="font-semibold">${order.total.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Placed</p>
                            <p className="font-semibold">{new Date(order.placedAt).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm space-y-2">
                          {order.delivery?.fulfillmentType === 'curbside' && (
                            <Badge className="bg-sky-600">CURBSIDE PICKUP</Badge>
                          )}
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-neutral-500" />
                            <div>
                              <p className="text-gray-600 text-xs uppercase tracking-wide">
                                {order.delivery?.fulfillmentType === 'curbside'
                                  ? 'Pickup location'
                                  : 'Delivery address'}
                              </p>
                              <p className="font-semibold text-neutral-900">
                                {order.delivery?.address || 'No address provided'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 mt-0.5 shrink-0 text-neutral-500" />
                            <div>
                              <p className="text-gray-600 text-xs uppercase tracking-wide">
                                {order.delivery?.fulfillmentType === 'curbside'
                                  ? 'Requested pickup'
                                  : 'Requested delivery'}
                              </p>
                              <p className="font-semibold text-neutral-900">
                                {formatDeliveryDate(order.delivery?.date)} · {formatDeliveryTime(order.delivery)}
                              </p>
                              {order.delivery?.estimatedArrival && (
                                <p className="text-xs text-neutral-600 mt-0.5">
                                  {order.delivery?.fulfillmentType === 'curbside'
                                    ? 'Ready from: '
                                    : 'Est. arrival: '}
                                  {order.delivery.estimatedArrival}
                                </p>
                              )}
                            </div>
                          </div>
                          {order.delivery?.notes && (
                            <p className="text-neutral-700 pl-6">
                              <span className="text-gray-600">Notes:</span> {order.delivery.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => viewOrderDetails(order)}
                          variant="outline"
                          size="sm"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order Details Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Order Details - {selectedOrder?.id}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Status Update */}
              <div className="flex items-center gap-4">
                <Label className="font-semibold">Order Status:</Label>
                <Select
                  value={selectedOrder.status}
                  onValueChange={(value) => handleStatusUpdate(selectedOrder.id, value)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery Request */}
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {selectedOrder.delivery?.fulfillmentType === 'curbside'
                    ? 'Curbside Pickup'
                    : 'Delivery Request'}
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  {selectedOrder.delivery?.fulfillmentType === 'curbside' && (
                    <Badge className="bg-sky-600">CURBSIDE PICKUP</Badge>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {selectedOrder.delivery?.fulfillmentType === 'curbside'
                        ? 'Pickup location'
                        : 'Delivery address'}
                    </p>
                    <p className="font-semibold text-neutral-900">{selectedOrder.delivery.address}</p>
                    {selectedOrder.delivery.street &&
                      selectedOrder.delivery?.fulfillmentType !== 'curbside' && (
                      <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-neutral-700">
                        <p><span className="text-gray-600">Street:</span> {selectedOrder.delivery.street}</p>
                        <p><span className="text-gray-600">City:</span> {selectedOrder.delivery.city}</p>
                        <p><span className="text-gray-600">State:</span> {selectedOrder.delivery.state}</p>
                        <p><span className="text-gray-600">ZIP:</span> {selectedOrder.delivery.zip}</p>
                      </div>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Date requested</p>
                      <p className="font-semibold">{formatDeliveryDate(selectedOrder.delivery.date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Time window chosen</p>
                      <p className="font-semibold">{formatDeliveryTime(selectedOrder.delivery)}</p>
                    </div>
                  </div>
                  {selectedOrder.delivery.estimatedArrival && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Estimated arrival</p>
                      <p className="font-semibold">{selectedOrder.delivery.estimatedArrival}</p>
                    </div>
                  )}
                  {selectedOrder.delivery.notes && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Delivery notes</p>
                      <p className="font-semibold">{selectedOrder.delivery.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Customer Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-semibold text-right">{getCustomerName(selectedOrder)}</span>
                  </div>
                  {selectedOrder.customerEmail && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        Email:
                      </span>
                      <a
                        href={`mailto:${selectedOrder.customerEmail}`}
                        className="font-semibold text-right text-neutral-900 underline"
                      >
                        {selectedOrder.customerEmail}
                      </a>
                    </div>
                  )}
                  {selectedOrder.customerPhone && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        Phone:
                      </span>
                      <a
                        href={`tel:${selectedOrder.customerPhone}`}
                        className="font-semibold text-right text-neutral-900 underline"
                      >
                        {selectedOrder.customerPhone}
                      </a>
                    </div>
                  )}
                  {selectedOrder.customerId && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600">Account ID:</span>
                      <span className="font-mono text-sm">{selectedOrder.customerId}</span>
                    </div>
                  )}
                  {selectedOrder.driverName && (
                    <div className="flex justify-between gap-4 border-t pt-2 mt-2">
                      <span className="text-gray-600">Assigned driver:</span>
                      <span className="font-semibold">{selectedOrder.driverName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Items
                </h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 border-b pb-3">
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1">
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-sm text-gray-600">{item.category} • THC: {item.thc}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${item.price} × {item.quantity}</p>
                        <p className="text-sm text-gray-600">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Information */}
              <div
                className={`rounded-lg p-4 border-2 ${
                  selectedOrder.payment.paymentStatus === 'pending_manual'
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <h3
                  className={`font-bold text-lg mb-3 flex items-center gap-2 ${
                    selectedOrder.payment.paymentStatus === 'pending_manual'
                      ? 'text-amber-900'
                      : 'text-green-900'
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  {selectedOrder.payment.paymentStatus === 'pending_manual'
                    ? 'Payment — charge at store / delivery'
                    : 'Payment (ProteusERP)'}
                </h3>
                {selectedOrder.payment.paymentStatus === 'pending_manual' && (
                  <p className="text-sm text-amber-800 mb-3 bg-amber-100/80 rounded-md px-3 py-2">
                    Run this card in your Proteus POS or terminal for{' '}
                    <strong>${selectedOrder.total.toFixed(2)}</strong>. Full card number and CVV are
                    below. Mark the order completed after charging to clear stored details.
                  </p>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Type:</span>
                    <span className="font-semibold capitalize">
                      {selectedOrder.payment.cardType || 'debit'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Card ending in:</span>
                    <span className="font-mono font-semibold">
                      {selectedOrder.payment.lastFour ||
                        (selectedOrder.payment.cardNumber
                          ? selectedOrder.payment.cardNumber.slice(-4)
                          : '****')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Cardholder:</span>
                    <span className="font-semibold">{selectedOrder.payment.cardName}</span>
                  </div>
                  {selectedOrder.payment.expiryDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Expiry:</span>
                      <span className="font-mono font-semibold">{selectedOrder.payment.expiryDate}</span>
                    </div>
                  )}
                  {selectedOrder.payment.billingZip && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Billing ZIP:</span>
                      <span className="font-mono font-semibold">{selectedOrder.payment.billingZip}</span>
                    </div>
                  )}
                  {selectedOrder.payment.manualCapture?.cardNumber && (
                    <div className="mt-3 rounded-md border border-amber-300 bg-white px-3 py-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">Card number (admin only):</span>
                        <span className="font-mono font-bold text-lg tracking-wider">
                          {selectedOrder.payment.manualCapture.cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
                        </span>
                      </div>
                      {selectedOrder.payment.manualCapture.cvv && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium">CVV (admin only):</span>
                          <span className="font-mono font-bold text-lg">
                            {selectedOrder.payment.manualCapture.cvv}
                          </span>
                        </div>
                      )}
                      <p className="text-xs text-amber-800">
                        {selectedOrder.payment.manualCapture.securityNote}
                      </p>
                    </div>
                  )}
                  {selectedOrder.payment.transactionId && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Transaction ID:</span>
                      <span className="font-mono font-semibold">
                        {selectedOrder.payment.transactionId}
                      </span>
                    </div>
                  )}
                  {selectedOrder.payment.authCode && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Auth code:</span>
                      <span className="font-mono font-semibold">
                        {selectedOrder.payment.authCode}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-700">Status:</span>
                    <span className="font-semibold capitalize">
                      {selectedOrder.payment.paymentStatus || 'approved'}
                    </span>
                  </div>
                  {selectedOrder.payment.processedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Captured:</span>
                      <span className="font-semibold">
                        {new Date(selectedOrder.payment.processedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Summary */}
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Order Summary
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">${selectedOrder.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Fee:</span>
                    <span className="font-semibold">
                      {selectedOrder.deliveryFee === 0 ? 'FREE' : `$${selectedOrder.deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-semibold">
                      {selectedOrder.tax === 0 ? 'Exempt' : `$${selectedOrder.tax.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total:</span>
                    <span>${selectedOrder.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
